import { useStore } from '@nanostores/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { billing } from '@/services/billing';
import {
  MASQ_READ_ONLY_MESSAGE,
  NO_BILLING_PERMISSION_MESSAGE,
  VERIFICATION_REJECTED_TOOLTIP,
} from '@/services/billing/messages';

export type BillingEditGuard = {
  /** Whether the current user may edit billing (has the permission, not read-only or rejected). */
  canEdit: boolean;
  /** Whether a billing edit/charge must be blocked. */
  isDisabled: boolean;
  /** The tooltip copy explaining why editing is blocked, or `''` when it isn't. */
  disabledMessage: string;
};

/**
 * The shared capability gate for billing edits and charges: blocks (with an explanatory message)
 * when the account's verification was rejected, the session is a read-only masquerade, or the user
 * lacks the `account/billing/edit` permission. Used by the credit-purchase forms and CTA gating so
 * every purchase surface enforces the same checks the credit-card form does.
 */
export function useBillingEditGuard(): BillingEditGuard {
  const { hasPermission } = usePermissions();
  const isVerificationRejected = useStore(billing.isVerificationRejected);
  const masqReadOnly = useStore(billing.isMasqReadOnly);
  const canEdit = hasPermission('account/billing/edit');

  let disabledMessage = '';
  if (isVerificationRejected) {
    disabledMessage = VERIFICATION_REJECTED_TOOLTIP;
  } else if (masqReadOnly) {
    disabledMessage = MASQ_READ_ONLY_MESSAGE;
  } else if (!canEdit) {
    disabledMessage = NO_BILLING_PERMISSION_MESSAGE;
  }

  return { canEdit, isDisabled: disabledMessage !== '', disabledMessage };
}
